const VERSION = '3.0.0';
const _formats = {};
const _locales = {};
const defaults = {
    currentLocale: 'en',
    zeroFormat: null,
    nullFormat: null,
    defaultFormat: '0,0',
    scalePercentBy100: true
};
const _options = Object.assign({}, defaults);
export class Numeral {
    constructor(input, value) {
        this._input = input;
        this._value = value;
    }
    static numeral(input) {
        let value;
        const { options, formats } = numeral;
        if (Numeral.isNumeral(input)) {
            value = input.value();
        }
        else if (input === 0 || typeof input === 'undefined') {
            value = 0;
        }
        else if (input === null || _.isNaN(input)) {
            value = null;
        }
        else if (typeof input === 'string') {
            if (options.zeroFormat && input === options.zeroFormat) {
                value = 0;
            }
            else if ((options.nullFormat && input === options.nullFormat) || !input.replace(/[^0-9]+/g, '').length) {
                value = null;
            }
            else {
                let unformatFunction;
                for (const kind in formats) {
                    const regexp = typeof formats[kind].regexps.unformat === 'function'
                        ? formats[kind].regexps.unformat()
                        : formats[kind].regexps.unformat;
                    if (regexp && input.match(regexp)) {
                        unformatFunction = formats[kind].unformat;
                        break;
                    }
                }
                unformatFunction = unformatFunction || _.stringToNumber;
                value = unformatFunction(input);
            }
        }
        else {
            value = Number(input) || null;
        }
        return new Numeral(input, value);
    }
    clone() {
        return numeral(this);
    }
    format(inputString, roundingFunction) {
        const value = this._value;
        const { options, formats } = numeral;
        const format = inputString || options.defaultFormat;
        let kind;
        let output;
        let formatFunction;
        roundingFunction = roundingFunction || Math.round;
        if (value === 0 && options.zeroFormat !== null) {
            output = options.zeroFormat;
        }
        else if (value === null && options.nullFormat !== null) {
            output = options.nullFormat;
        }
        else {
            for (kind in formats) {
                if (format.match(formats[kind].regexps.format)) {
                    formatFunction = formats[kind].format;
                    break;
                }
            }
            formatFunction = formatFunction || _.numberToFormat;
            output = formatFunction(value, format, roundingFunction);
        }
        return output;
    }
    value() {
        return this._value;
    }
    input() {
        return this._input;
    }
    set(value) {
        this._value = numeral(value).value();
        return this;
    }
    add(v) {
        const value = numeral(v).value();
        const { _value } = this;
        const corrFactor = _.correctionFactor.call(null, _value, value);
        const cback = (accum, curr) => {
            return accum + Math.round(corrFactor * curr);
        };
        this._value = _.reduce([_value, value], cback, 0) / corrFactor;
        return this;
    }
    subtract(v) {
        const value = numeral(v).value();
        const corrFactor = _.correctionFactor.call(null, this._value, value);
        function cback(accum, curr) {
            return accum - Math.round(corrFactor * curr);
        }
        this._value = _.reduce([value], cback, Math.round(this._value * corrFactor)) / corrFactor;
        return this;
    }
    multiply(value) {
        function cback(accum, curr) {
            const corrFactor = _.correctionFactor(accum, curr);
            return ((Math.round(accum * corrFactor) * Math.round(curr * corrFactor)) / Math.round(corrFactor * corrFactor));
        }
        this._value = _.reduce([this._value, numeral(value).value()], cback, 1);
        return this;
    }
    divide(value) {
        function cback(accum, curr) {
            const corrFactor = _.correctionFactor(accum, curr);
            return Math.round(accum * corrFactor) / Math.round(curr * corrFactor);
        }
        this._value = _.reduce([this._value, numeral(value).value()], cback);
        return this;
    }
    difference(value) {
        return Math.abs(numeral(this._value)
            .subtract(value)
            .value());
    }
    static isNumeral(obj) {
        return obj instanceof Numeral;
    }
}
Numeral.version = VERSION;
const _ = {
    numberToFormat(value, format, roundingFunction) {
        const { locales } = numeral;
        const locale = locales[numeral.options.currentLocale];
        let negP = false;
        let optDec = false;
        let leadingCount = 0;
        let abbr = '';
        const trillion = 1000000000000;
        const billion = 1000000000;
        const million = 1000000;
        const thousand = 1000;
        let decimal = '';
        let neg = false;
        let abbrForce;
        let signed = -1;
        let output;
        value = value || 0;
        const abs = Math.abs(value);
        if (format.includes('(')) {
            negP = true;
            format = format.replace(/[(|)]/g, '');
        }
        else if (format.includes('+') || format.includes('-')) {
            signed = format.includes('+') ? format.indexOf('+') : value < 0 ? format.indexOf('-') : -1;
            format = format.replace(/[+|-]/g, '');
        }
        if (format.includes('a')) {
            abbrForce = format.match(/a(k|m|b|t)?/);
            abbrForce = abbrForce ? abbrForce[1] : false;
            if (format.includes(' a')) {
                abbr = ' ';
            }
            format = format.replace(new RegExp(`${abbr}a[kmbt]?`), '');
            if ((abs >= trillion && !abbrForce) || abbrForce === 't') {
                abbr += locale.abbreviations.trillion;
                value = value / trillion;
            }
            else if ((abs < trillion && abs >= billion && !abbrForce) || abbrForce === 'b') {
                abbr += locale.abbreviations.billion;
                value = value / billion;
            }
            else if ((abs < billion && abs >= million && !abbrForce) || abbrForce === 'm') {
                abbr += locale.abbreviations.million;
                value = value / million;
            }
            else if ((abs < million && abs >= thousand && !abbrForce) || abbrForce === 'k') {
                abbr += locale.abbreviations.thousand;
                value = value / thousand;
            }
        }
        if (format.includes('[.]')) {
            optDec = true;
            format = format.replace('[.]', '.');
        }
        let int = value.toString().split('.')[0];
        let precision = format.split('.')[1];
        const thousands = format.indexOf(',');
        leadingCount = (format
            .split('.')[0]
            .split(',')[0]
            .match(/0/g) || []).length;
        if (precision) {
            if (precision.includes('[')) {
                precision = precision.replace(']', '');
                precision = precision.split('[');
                decimal = _.toFixed(value, precision[0].length + precision[1].length, roundingFunction, precision[1].length);
            }
            else {
                decimal = _.toFixed(value, precision.length, roundingFunction);
            }
            int = decimal.split('.')[0];
            if (decimal.includes('.')) {
                decimal = locale.delimiters.decimal + decimal.split('.')[1];
            }
            else {
                decimal = '';
            }
            if (optDec && Number(decimal.slice(1)) === 0) {
                decimal = '';
            }
        }
        else {
            int = _.toFixed(value, 0, roundingFunction);
        }
        if (abbr && !abbrForce && Number(int) >= 1000 && abbr !== locale.abbreviations.trillion) {
            int = String(Number(int) / 1000);
            switch (abbr) {
                case locale.abbreviations.thousand:
                    abbr = locale.abbreviations.million;
                    break;
                case locale.abbreviations.million:
                    abbr = locale.abbreviations.billion;
                    break;
                case locale.abbreviations.billion:
                    abbr = locale.abbreviations.trillion;
                    break;
            }
        }
        if (int.includes('-')) {
            int = int.slice(1);
            neg = true;
        }
        if (int.length < leadingCount) {
            for (let i = leadingCount - int.length; i > 0; i--) {
                int = `0${int}`;
            }
        }
        if (thousands > -1) {
            int = int.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${locale.delimiters.thousands}`);
        }
        if (format.indexOf('.') === 0) {
            int = '';
        }
        output = int + decimal + (abbr ? abbr : '');
        if (negP) {
            output = (negP && neg ? '(' : '') + output + (negP && neg ? ')' : '');
        }
        else {
            if (signed >= 0) {
                output = signed === 0 ? (neg ? '-' : '+') + output : output + (neg ? '-' : '+');
            }
            else if (neg) {
                output = `-${output}`;
            }
        }
        return output;
    },
    stringToNumber(s) {
        const { locales, options } = numeral;
        const locale = locales[options.currentLocale];
        const stringOriginal = s;
        const abbreviations = {
            thousand: 3,
            million: 6,
            billion: 9,
            trillion: 12
        };
        let value;
        if (options.zeroFormat && s === options.zeroFormat) {
            value = 0;
        }
        else if ((options.nullFormat && s === options.nullFormat) || !s.replace(/[^0-9]+/g, '').length) {
            value = null;
        }
        else {
            value = 1;
            if (locale.delimiters.decimal !== '.') {
                s = s.replace(/\./g, '').replace(locale.delimiters.decimal, '.');
            }
            for (const abbreviation in abbreviations) {
                const regexp = new RegExp(`[^a-zA-Z]${locale.abbreviations[abbreviation]}(?:\\)|(\\${locale.currency.symbol})?(?:\\))?)?$`);
                if (stringOriginal.match(regexp)) {
                    value *= Math.pow(10, abbreviations[abbreviation]);
                    break;
                }
            }
            value *= (s.split('-').length + Math.min(s.split('(').length - 1, s.split(')').length - 1)) % 2 ? 1 : -1;
            s = s.replace(/[^0-9.]+/g, '');
            value *= Number(s);
        }
        return value;
    },
    isNaN(value) {
        return typeof value === 'number' && isNaN(value);
    },
    insert(s, subString, start) {
        return s.slice(0, start) + subString + s.slice(start);
    },
    reduce(array, callback, initialValue) {
        if (this === null) {
            throw new TypeError('Array.prototype.reduce called on null or undefined');
        }
        if (typeof callback !== 'function') {
            throw new TypeError(`${callback} is not a function`);
        }
        const t = Object(array);
        const len = t.length >>> 0;
        let k = 0;
        let value;
        if (initialValue) {
            value = initialValue;
        }
        else {
            while (k < len && !(k in t)) {
                k++;
            }
            if (k >= len) {
                throw new TypeError('Reduce of empty array with no initial value');
            }
            value = t[k++];
        }
        for (; k < len; k++) {
            if (k in t) {
                value = callback(value, t[k], k, t);
            }
        }
        return value;
    },
    multiplier(x) {
        const parts = x.toString().split('.');
        return parts.length < 2 ? 1 : Math.pow(10, parts[1].length);
    },
    correctionFactor(...args) {
        return args.reduce((accum, next) => {
            const mn = _.multiplier(next);
            return accum > mn ? accum : mn;
        }, 1);
    },
    toFixed(value, maxDecimals, roundingFunction, optionals = 0) {
        const splitValue = value.toString().split('.');
        const minDecimals = maxDecimals - optionals;
        const boundedPrecision = splitValue.length === 2 ? Math.min(Math.max(splitValue[1].length, minDecimals), maxDecimals) : minDecimals;
        const power = Math.pow(10, boundedPrecision);
        let output = (roundingFunction(+`${value}e+${boundedPrecision}`) / power).toFixed(boundedPrecision);
        if (optionals > maxDecimals - boundedPrecision) {
            const optionalsRegExp = new RegExp(`\\.?0{1,${optionals - (maxDecimals - boundedPrecision)}}$`);
            output = output.replace(optionalsRegExp, '');
        }
        return output;
    }
};
const helperFuncs = _;
export function numeral(input) {
    return Numeral.numeral(input);
}
(function (numeral) {
    numeral.isNumeral = Numeral.isNumeral;
    numeral.version = Numeral.version;
    numeral.options = _options;
    numeral.formats = _formats;
    numeral.locales = _locales;
    numeral._ = helperFuncs;
    numeral.locale = (key) => {
        if (key) {
            numeral.options.currentLocale = key.toLowerCase();
        }
        return numeral.options.currentLocale;
    };
    numeral.localeData = (key) => {
        if (!key) {
            return numeral.locales[numeral.options.currentLocale];
        }
        key = key.toLowerCase();
        if (!numeral.locales[key]) {
            throw new Error(`Unknown locale : ${key}`);
        }
        return numeral.locales[key];
    };
    numeral.reset = () => {
        Object.assign(numeral.options, defaults);
    };
    numeral.zeroFormat = (format) => {
        numeral.options.zeroFormat = typeof format === 'string' ? format : null;
    };
    numeral.nullFormat = (format) => {
        numeral.options.nullFormat = typeof format === 'string' ? format : null;
    };
    numeral.defaultFormat = (format) => {
        numeral.options.defaultFormat = typeof format === 'string' ? format : '0.0';
    };
    numeral.register = (type, name, format) => {
        name = name.toLowerCase();
        if (numeral[`${type}s`][name]) {
            throw new TypeError(`${name} ${type} already registered.`);
        }
        numeral[`${type}s`][name] = format;
        return format;
    };
    numeral.validate = (val, culture) => {
        let _thousandSep;
        let _valArray;
        let localeData;
        let temp;
        val = val.trim();
        if (val.match(/^\d+$/)) {
            return true;
        }
        if (val === '') {
            return false;
        }
        try {
            localeData = numeral.localeData(culture);
        }
        catch (e) {
            localeData = numeral.localeData(numeral.locale());
        }
        const _currSymbol = localeData.currency.symbol;
        const _abbrObj = localeData.abbreviations;
        const _decimalSep = localeData.delimiters.decimal;
        if (localeData.delimiters.thousands === '.') {
            _thousandSep = '\\.';
        }
        else {
            _thousandSep = localeData.delimiters.thousands;
        }
        temp = val.match(/^[^\d]+/);
        if (temp !== null) {
            val = val.substr(1);
            if (temp[0] !== _currSymbol) {
                return false;
            }
        }
        temp = val.match(/[^\d]+$/);
        if (temp !== null) {
            val = val.slice(0, -1);
            if (temp[0] !== _abbrObj.thousand &&
                temp[0] !== _abbrObj.million &&
                temp[0] !== _abbrObj.billion &&
                temp[0] !== _abbrObj.trillion) {
                return false;
            }
        }
        const _thousandRegEx = new RegExp(`${_thousandSep}{2}`);
        if (!val.match(/[^\d.,]/g)) {
            _valArray = val.split(_decimalSep);
            if (_valArray.length > 2) {
                return false;
            }
            else {
                if (_valArray.length < 2) {
                    return !!_valArray[0].match(/^\d+.*\d$/) && !_valArray[0].match(_thousandRegEx);
                }
                else {
                    if (_valArray[0].length === 1) {
                        return (!!_valArray[0].match(/^\d+$/) &&
                            !_valArray[0].match(_thousandRegEx) &&
                            !!_valArray[1].match(/^\d+$/));
                    }
                    else {
                        return (!!_valArray[0].match(/^\d+.*\d$/) &&
                            !_valArray[0].match(_thousandRegEx) &&
                            !!_valArray[1].match(/^\d+$/));
                    }
                }
            }
        }
        return false;
    };
})(numeral || (numeral = {}));
numeral.register('locale', 'en', {
    delimiters: {
        thousands: ',',
        decimal: '.'
    },
    abbreviations: {
        thousand: 'k',
        million: 'm',
        billion: 'b',
        trillion: 't'
    },
    ordinal(val) {
        const b = val % 10;
        return ~~((val % 100) / 10) === 1 ? 'th' : b === 1 ? 'st' : b === 2 ? 'nd' : b === 3 ? 'rd' : 'th';
    },
    currency: {
        symbol: '$'
    }
});
